import React, { useState, useEffect, useMemo } from 'react';
import MacTable from '../ui/MacTable';
import MacClassicWindow from '../ui/MacClassicWindow';
import VisitLink from '../ui/VisitLink';
import FindingsTable from '../ui/FindingsTable';
import '../../styles/style.css';
import '../../styles/report-modal.css';

// Import utilities
import { getData } from '../../utils/dataUtils';
import { formatDate } from '../../utils/dateUtils';
import { getAwardInfo, sortFindingsBySeverity } from '../../utils/sortUtils';
import { TABLE_HEADERS } from '../../utils/constants';

const ContestSection = () => {
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContest, setSelectedContest] = useState(null);
  const [showAllContests, setShowAllContests] = useState(false);

  useEffect(() => {
    const loadContests = async () => {
      try {
        setLoading(true);
        
        try {
          // Get data from local JSON file with sorting
          const data = getData('contests', { sort: 'date', sortOrder: 'desc' });
          setContests(data);
        } catch (err) {
          setError('Failed to load contest data.');
        }
        
        setLoading(false);
      } catch (err) {
        setError('An unexpected error occurred.');
        setLoading(false);
      }
    };

    loadContests();
  }, []);

  const handleViewFindings = (item) => {
    // Sort findings by severity if they exist
    const sortedFindings = item.myFindings ? 
      sortFindingsBySeverity(item.myFindings) : 
      [];
      
    setSelectedContest({
      contest: item.contest,
      project: item.project,
      findings: sortedFindings
    });
  };

  const closeFindings = () => {
    setSelectedContest(null);
  };

  const handleModalClick = (e) => {
    // Close modal when clicking outside the modal content
    if (e.target.className === 'report-modal') {
      closeFindings();
    }
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedContest) {
        closeFindings();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedContest]);

  const toggleShowAllContests = (e) => {
    e.preventDefault(); // Prevent the default anchor behavior
    setShowAllContests(!showAllContests);
  };

  // Memoize displayed contests based on view state
  // Preview: show award winners + pending contests (not confirmed participants)
  const displayedContests = useMemo(() => {
    if (showAllContests) return contests;
    return contests
      .filter(c => c.award <= 5 || (c.myFindings && c.myFindings.length === 0))
      .slice(0, 5);
  }, [contests, showAllContests]);

  const renderRow = (item, index) => {
    // Get award class and label
    const { awardClass, awardLabel } = getAwardInfo(item.award);
    
    // Handle both project/contest field names
    const contestName = item.contest;
    const contestUrl = item.contestUrl;
    const contestReportUrl = item.contestReportUrl;
    
    return (
      <tr key={item.id || index}>
        <td>
          {contestUrl ? 
            <a href={contestUrl} target="_blank" rel="noopener noreferrer">{contestName}</a> : 
            contestName
          }
        </td>
        <td>{item.type}</td>
        <td>
          <span className={`award ${awardClass}`}>
            {awardLabel}
          </span>
        </td>
        <td>
          {item.platform}
        </td>
        <td>{formatDate(item.date)}</td>
        <td>
          {item.myFindings && item.myFindings.length === 0 ? (
            <img src="images/clock-face-four-oclock_1f553.gif" alt="pending" loading="lazy" width="16" height="16" />
          ) : contestReportUrl ? (
            <VisitLink
              url={contestReportUrl}
              placeholder="view"
            />
          ) : (
            <span className="unpublished">Undisclosed</span>
          )}
        </td>
        <td>
          {item.myFindings && item.myFindings.length === 0 ? (
            <img src="images/clock-face-four-oclock_1f553.gif" alt="pending" loading="lazy" width="16" height="16" />
          ) : item.myFindings && item.myFindings.length > 0 ? (
            <span
              onClick={() => handleViewFindings(item)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewFindings(item); } }}
              className="visit-link"
              role="button"
              tabIndex={0}
            >
              view ↗
            </span>
          ) : (
            <span className="no-findings">None</span>
          )}
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <section id="contests">
        <div className="container">
          <h2 className="topic">Contest Participation</h2>
          <div className="loading">Loading contests...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="contests">
        <div className="container">
          <h2 className="topic">Contest Participation</h2>
          <div className="error">{error}</div>
        </div>
      </section>
    );
  }

  return (
    <section id="contests">
      <div className="container">
        <h2 className="topic">Contest Participation</h2>
        <MacTable
          title=""
          headers={TABLE_HEADERS.CONTESTS}
          data={displayedContests}
          renderRow={renderRow}
          showViewMore={contests.length > 5}
          onViewMoreClick={toggleShowAllContests}
          viewMoreText={showAllContests ? 'Show Less' : 'View More'}
        />
        
        {selectedContest && (
          <div className="report-modal" onClick={handleModalClick}>
            <div className="report-modal-content">
              <MacClassicWindow 
                title={`${selectedContest.contest || selectedContest.project} - My Findings`}
                onClose={closeFindings}
              >
                <FindingsTable 
                  title=""
                  findings={selectedContest.findings} 
                />
              </MacClassicWindow>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ContestSection;
